import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useEinfuehrungStore } from '../../stores/einfuehrungStore';
import { TOUREN, type TourRechte } from '../../lib/touren';
import { istEinreicher, istRedaktion, istTeam, kannVorbereiten } from '../../lib/rollen';
import { EinfuehrungsModal } from './EinfuehrungsModal';
import { EinfuehrungsTour } from './EinfuehrungsTour';

/** Der Rundgang beginnt auf der Startseite — nur dort sind alle Ziele vorhanden. */
const STARTSEITE = '/';

/**
 * Klammer um Begrüßung und Tour — hängt einmal im Layout und entscheidet,
 * wann welches von beidem erscheint.
 */
export function Einfuehrung() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const {
    geladen, begruessung, angefordert, tourId,
    faellig, begruessungZeigen, spaeter, tourStarten,
  } = useEinfuehrungStore();

  const rechte: TourRechte = useMemo(() => ({
    vorbereiten: kannVorbereiten(user),
    einreicher: istEinreicher(user),
    team: istTeam(user),
    redaktion: istRedaktion(user),
    admin: user?.role === 'admin',
  }), [user]);

  // Beim ersten Mal von selbst begrüßen.
  useEffect(() => {
    if (!user || user.ohneRolle || !geladen) return;
    if (faellig()) begruessungZeigen();
  }, [user, geladen, faellig, begruessungZeigen]);

  const starten = useCallback(
    (id: string) => {
      // Der Rundgang braucht die Startseite; die Seiten-Touren bleiben, wo sie sind.
      if (id === 'einfuehrung' && pathname !== STARTSEITE) navigate(STARTSEITE);
      tourStarten(id);
    },
    [pathname, navigate, tourStarten],
  );

  // Angeforderte Tour starten, sobald der Seitenwechsel durch ist.
  useEffect(() => {
    if (!angefordert) return;
    if (!TOUREN[angefordert]) return;
    starten(angefordert);
  }, [angefordert, starten]);

  const vorname = (user?.fullName ?? '').trim().split(/\s+/)[0] ?? '';

  return (
    <>
      <EinfuehrungsModal
        offen={begruessung && !tourId}
        vorname={vorname}
        rechte={rechte}
        onTour={() => starten('einfuehrung')}
        onSpaeter={spaeter}
      />
      <EinfuehrungsTour rechte={rechte} />
    </>
  );
}
